import { getCookie } from 'cookies-next';

import { getLegacyUserSession, getUserProfileInfo } from 'services/user';

import { getUserAvatarUrl } from '../image';
import { removeFromLocalStorage, setLocalStorage } from '../localStorage';
import { parseToBool } from '../string';
import { isDev } from '../utils';

import COOKIES from 'constants/cookies';
import LOCAL_STORAGE from 'constants/localStorage';
import USER from 'constants/user';

export const clearLoginStorage = async () => {
  const { deleteCookie } = await import('cookies-next');

  removeFromLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN);
  removeFromLocalStorage(LOCAL_STORAGE.DEPRECATED_LOGGED_IN_USER);
  removeFromLocalStorage(LOCAL_STORAGE.DEPRECATED_USER_PHOTO);
  removeFromLocalStorage(LOCAL_STORAGE.IS_SUPIX_COLLECTED);
  removeFromLocalStorage(LOCAL_STORAGE.SHOWED_WELCOME_NOTIFICATION);
  removeFromLocalStorage(LOCAL_STORAGE.WEBPUSH_HASH);
  deleteCookie(COOKIES.IS_USER_LOGGED);
};

export const filterUsersByWord = (users, word) => {
  if (!word || word.length === 0) {
    return users;
  }

  return users.filter(({ userUsername }) =>
    userUsername?.toLowerCase().includes(word)
  );
};

/**
 * Returned when the session could not be verified either way — network failure,
 * 5xx, or an unreachable endpoint. Distinct from `null`, which means the API
 * positively rejected the token.
 *
 * Callers MUST NOT clear the session on this value. Treating "could not verify"
 * as "not logged in" is what logged every user out when the origin changed: the
 * legacy `/User/me/` endpoint 404'd on the new origin and the 404 was read as a
 * failed auth check.
 */
export const SESSION_INDETERMINATE = Symbol('SESSION_INDETERMINATE');

const getLoggedInUserFromServer = async () => {
  try {
    // Deliberately no `ignoreErrors` — it collapses every failure to `null` and
    // loses the status code, which is the only thing that distinguishes a dead
    // token from an unreachable API.
    const userProfile = await getUserProfileInfo();

    if (!userProfile) {
      return SESSION_INDETERMINATE;
    }

    const LoggedInUser = (await import('lib/LoggedInUser')).default;

    return new LoggedInUser(userProfile);
  } catch (error) {
    /**
     * Only 401 proves the token is dead. 403/404/5xx/timeouts prove nothing.
     *
     * `RequestError` stores status as a STRING (`errors/RequestError.js`), so
     * this must not be a strict comparison against the number 401 — that is
     * always false, and a revoked token would never be cleared.
     */
    return Number(error?.status) === 401 ? null : SESSION_INDETERMINATE;
  }
};

/**
 * Keeps the legacy monolith session in step with ours, so PHP-served pages on
 * the same host recognise a user who logged in on a Next page.
 *
 * BEST-EFFORT BY CONTRACT. It resolves to nothing and throws nothing, and must
 * never influence whether the user is considered authenticated.
 *
 * That distinction is the entire point. The previous implementation did:
 *
 *     if (!legacyUserSession?.token) return null;
 *
 * which reads "the monolith is not reachable from this origin" as "this user is
 * not logged in". On the EC2 origin the monolith answers, so it worked; on EKS
 * the endpoint 404s, and every user was logged out on page load. Same class of
 * bug as collapsing a 5xx into `null` in `getLoggedInUserFromServer` — failing
 * to VERIFY is not failing to AUTHENTICATE.
 *
 * Structured so it cannot regress: there is no return value to branch on.
 *
 * Awaited, not fire-and-forget, because the monolith may hand back a different
 * token and `getUserProfileInfo` reads the bearer token from localStorage — the
 * write has to land before the API call reads it.
 *
 * Deleted, along with the service functions, once the monolith no longer serves
 * pages under the app host. No flag to retire: on origins without a monolith
 * this is already a no-op.
 */
const syncLegacySession = async ({ token }) => {
  // No monolith on localhost — skip the guaranteed failure and console noise.
  if (isDev()) {
    return;
  }

  try {
    /**
     * One request, where the previous code made two: this one with the real
     * token, plus a duplicate with `token: null` on every session check. Both
     * hit the same endpoint with `credentials: 'include'`, so the Set-Cookie
     * side effect the second one existed for already happens here. Reviewers:
     * this is the one behavioural simplification in this PR — flag it if the
     * monolith is known to treat a tokenless call differently.
     */
    const legacySession = await getLegacyUserSession({ token });

    if (legacySession?.token) {
      setLocalStorage(LOCAL_STORAGE.ACCESS_TOKEN, legacySession.token);
    }
  } catch {
    // Intentionally swallowed. See the contract above.
  }
};

export const getLoggedInUser = async ({ token }) => {
  const isUserLogged = parseToBool(getCookie(COOKIES.IS_USER_LOGGED));

  if (!isUserLogged || !token) {
    return null;
  }

  await syncLegacySession({ token });

  return getLoggedInUserFromServer();
};

/**
 * In URL the userUsername starts with the prefix '@', so we remove this to
 * be used in specific cases like request params
 */
export const getUserUsernameWithoutPrefix = (userUsername) =>
  userUsername.substring(1);

export const isUserAdmin = (userTypeName) => userTypeName === USER.TYPES.ADMIN;

export const isGenieUser = (userId) => userId === 0;

export const saveUserAsLegacyModeToLocalStorage = (user) => {
  removeFromLocalStorage('me.checked');
  setLocalStorage(
    LOCAL_STORAGE.DEPRECATED_USER_PHOTO,
    getUserAvatarUrl(user.userPhoto)
  );
};

export const toUsernameWithAtSign = (username) =>
  username ? `@${username}` : null;
